import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '../../services/subscription';
import type { SubscriptionPlan } from '../../types/subscription';
import faqService, { getTranslatedText } from '../../services/faq';
import type { FaqQuestion } from '../../types/faq';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import blogService from '../../services/blog';

export default function HomePage() {
  const { t } = useTranslation('marketing');
  const { isAuthenticated } = useAuthStore();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'en';
  const { createLink } = useLanguageLink();

  const { data: featuredPosts = [] } = useQuery({
    queryKey: ['featured-blog-posts', currentLang],
    queryFn: () => blogService.getFeaturedBlogPosts(currentLang),
    staleTime: 5 * 60 * 1000,
  });

  const features = [
    {
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('features.quality_score.title'),
      description: t('features.quality_score.description'),
    },
    {
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: t('features.cross_campaign.title'),
      description: t('features.cross_campaign.description'),
    },
    {
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: t('features.visual_analytics.title'),
      description: t('features.visual_analytics.description'),
    },
    {
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: t('features.advanced_security.title'),
      description: t('features.advanced_security.description'),
    },
    {
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: t('features.realtime.title'),
      description: t('features.realtime.description'),
    },
    {
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: t('features.team_collab.title'),
      description: t('features.team_collab.description'),
    },
  ];

  const howItWorksSteps = [
    {
      step: '01',
      title: t('how_it_works.step1.title'),
      description: t('how_it_works.step1.description'),
      icon: (
        <svg className="w-16 h-16 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
    {
      step: '02',
      title: t('how_it_works.step2.title'),
      description: t('how_it_works.step2.description'),
      icon: (
        <svg className="w-16 h-16 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
    },
    {
      step: '03',
      title: t('how_it_works.step3.title'),
      description: t('how_it_works.step3.description'),
      icon: (
        <svg className="w-16 h-16 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
    },
    {
      step: '04',
      title: t('how_it_works.step4.title'),
      description: t('how_it_works.step4.description'),
      icon: (
        <svg className="w-16 h-16 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
    },
  ];

  const stats = [
    { value: '99.9%', label: t('stats.uptime_label') },
    { value: t('stats.requests'), label: t('stats.requests_label') },
    { value: t('stats.campaigns'), label: t('stats.campaigns_label') },
    { value: t('stats.countries'), label: t('stats.countries_label') },
  ];

  const testimonials = [
    {
      quote: t('testimonials.quote1.text'),
      author: t('testimonials.quote1.author'),
      role: t('testimonials.quote1.role'),
      company: t('testimonials.quote1.company'),
      avatar: "AM",
    },
    {
      quote: t('testimonials.quote2.text'),
      author: t('testimonials.quote2.author'),
      role: t('testimonials.quote2.role'),
      company: t('testimonials.quote2.company'),
      avatar: "СК",
    },
    {
      quote: t('testimonials.quote3.text'),
      author: t('testimonials.quote3.author'),
      role: t('testimonials.quote3.role'),
      company: t('testimonials.quote3.company'),
      avatar: "МР",
    },
  ];

  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Fetch subscription plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await subscriptionService.getAvailablePlans();
        // Filter out Free Trial, only show paid plans
        const paidPlans = response.plans.filter(plan => plan.slug !== 'free-trial');
        setSubscriptionPlans(paidPlans);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const [faqQuestions, setFaqQuestions] = useState<FaqQuestion[]>([]);
  const [faqsLoading, setFaqsLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Fetch FAQs from database
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setFaqsLoading(true);
        const response = await faqService.getPublicFaqs('general');

        // Get questions from the "General" category
        const generalCategory = response.categories.find(
          cat => cat.slug === 'general'
        );

        if (generalCategory && generalCategory.questions) {
          setFaqQuestions(generalCategory.questions);
        }
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title={currentLang === 'ru'
          ? 'Защита рекламы от ботов и скликивания | Axetra'
          : 'Traffic Filtering & Bot Detection Platform'}
        description={currentLang === 'ru'
          ? 'Защитите рекламный бюджет от ботов, скликивания и фейковых лидов. ML-фильтрация трафика. Попробуйте бесплатно 14 дней.'
          : 'Protect your Facebook, Google, and TikTok ads from bots, click fraud & proxies. ML-powered traffic filtering trusted by 1,000+ marketers. Try free 14 days.'}
        canonical={`/${currentLang}`}
        lang={currentLang}
        hreflang={[
          { lang: 'en', href: 'https://www.axetra.io/en' },
          { lang: 'ru', href: 'https://www.axetra.io/ru' },
          { lang: 'de', href: 'https://www.axetra.io/de' },
        ]}
        keywords={currentLang === 'ru'
          ? 'защита от скликивания, защита от ботов в рекламе, антифрод реклама, защита от кликфрода, фильтрация трафика'
          : 'traffic filtering, bot detection, click fraud protection, facebook ads protection, google ads protection, tiktok ads filtering, proxy detection, ad fraud prevention'}
        ogImage="/images/og-homepage.jpg"
      />

      {/* Schema Markup */}
      <SchemaMarkup type="organization" />
      <SchemaMarkup
        type="softwareApplication"
        name="Axetra Traffic Filtering Platform"
        price="39"
        ratingValue="4.8"
        reviewCount="150"
      />
      <SchemaMarkup
        type="faq"
        questions={faqQuestions.map(faq => ({
          question: getTranslatedText(faq.question, faq.question_translations, currentLang),
          answer: getTranslatedText(faq.answer, faq.answer_translations, currentLang),
        }))}
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-50 via-blue-50 to-primary-100 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse-slow"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="container-marketing px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                  {t('hero.headline')}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8">
                {t('hero.subheadline')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <Link to={createLink('register')} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-800 text-white rounded-lg font-semibold text-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
                  {t('hero.cta_primary')}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link to={createLink('demo')} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-lg font-semibold text-lg hover:border-primary-300 hover:bg-gray-50 transition-all duration-200">
                  <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  {t('hero.cta_secondary')}
                </Link>
              </div>
              <p className="text-sm text-gray-600 flex items-center justify-center lg:justify-start gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t('hero.social_proof', { count: '500' })}
              </p>
            </div>

            {/* Right Column - Hero Image */}
            <div className="hidden lg:block relative">
              <div className="relative">
                {/* Decorative background elements */}
                <div className="absolute -top-4 -right-4 w-72 h-72 bg-primary-200 rounded-3xl opacity-20 blur-2xl"></div>
                <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-blue-200 rounded-3xl opacity-20 blur-2xl"></div>

                {/* Main Image Container */}
                <div className="relative bg-white rounded-2xl shadow-2xl p-2 transform hover:scale-105 transition-transform duration-300">
                  <img
                    src="/images/hero/dashboard-preview.svg"
                    alt="Axetra Dashboard - Bot Detection and Traffic Analytics Platform"
                    className="w-full h-auto rounded-xl shadow-lg"
                    loading="eager"
                  />

                  {/* Floating accent badges */}
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-bounce-slow">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">99.8%</div>
                        <div className="text-sm text-gray-600">Detection Rate</div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-lg p-4 animate-bounce-slow" style={{ animationDelay: '0.5s' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-900">&lt;50ms</div>
                        <div className="text-sm text-gray-600">Response Time</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="container-marketing">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-800 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-500 font-medium text-sm uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50 scroll-mt-header">
        <div className="container-marketing">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('features.title')}
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:border-primary-300 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="mb-6 w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container-marketing">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('how_it_works.title')}
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('how_it_works.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative text-center">
                {/* Connector line sits outside the card so it spans between steps */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[62%] w-full h-0.5 bg-gradient-to-r from-primary-200 to-blue-200 z-10"></div>
                )}
                <div className="relative z-20 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 h-full">
                  <div className="relative mb-4 inline-flex items-center justify-center">
                    <div className="absolute inset-0 bg-primary-100 rounded-full blur-xl opacity-50"></div>
                    <div className="relative bg-primary-50 rounded-full p-4 border-2 border-primary-200">
                      {step.icon}
                    </div>
                  </div>
                  <div className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-primary-500 to-primary-800 mb-3">{step.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-primary-50 to-slate-100">
        <div className="container-marketing">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('testimonials.title')}
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 border-l-4 border-l-primary-400"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-primary-800 flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-sm text-primary-600">{testimonial.company}</div>
                  </div>
                </div>
                <p className="text-gray-700 italic leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex gap-1 mt-6">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section className="py-20 bg-white">
        <div className="container-marketing">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('pricing.title')}
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {plansLoading ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <div key={index} className="rounded-2xl p-8 border-2 border-gray-200 bg-white animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-10 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-6"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              subscriptionPlans.map((plan, index) => {
                // Mark the middle plan (Pro) as popular
                const isPopular = index === 1;

                // Format price
                const priceDisplay = plan.price === 0 ? t('pricing.free') : `$${Math.round(plan.price)}`;

                // Format requests (convert to K or show unlimited)
                const requestsDisplay = plan.limits.requests_per_month === -1
                  ? t('pricing.unlimited')
                  : `${(plan.limits.requests_per_month / 1000).toFixed(0)}K`;

                // Format campaigns
                const campaignsDisplay = plan.limits.campaigns === -1
                  ? t('pricing.unlimited')
                  : plan.limits.campaigns;

                const features = `${campaignsDisplay} ${t('pricing.campaigns')}, ${requestsDisplay} ${t('pricing.requests')}`;

                return (
                  <div
                    key={plan.id}
                    className={`rounded-2xl p-8 border-2 text-center transition-all duration-300 hover:-translate-y-2 ${
                      isPopular
                        ? 'border-primary-500 bg-gradient-to-br from-primary-50 to-blue-100 shadow-xl scale-105'
                        : 'border-gray-200 bg-white hover:border-primary-300'
                    }`}
                  >
                    {isPopular && (
                      <div className="inline-block px-4 py-1 bg-gradient-to-r from-primary-600 to-primary-800 text-white text-sm font-semibold rounded-full mb-4">
                        {t('pricing.most_popular')}
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-gray-900 mb-2">{priceDisplay}</div>
                    <p className="text-gray-600 mb-6">{features}</p>
                    <Link
                      to={createLink('pricing')}
                      className={`block w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                        isPopular
                          ? 'bg-gradient-to-r from-primary-600 to-primary-800 text-white hover:shadow-xl'
                          : 'bg-white text-primary-600 border-2 border-primary-200 hover:border-primary-400'
                      }`}
                    >
                      {t('pricing.learn_more')}
                    </Link>
                  </div>
                );
              })
            )}
          </div>

          <div className="text-center">
            <Link to={createLink('pricing')} className="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center gap-2">
              {t('pricing.view_detailed')}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-marketing max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('faq.title')}
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {faqsLoading ? (
              <div className="text-center py-8 text-gray-600">Loading FAQs...</div>
            ) : faqQuestions.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No FAQs available.</div>
            ) : (
              faqQuestions.map((faq, index) => {
                const translatedQuestion = getTranslatedText(
                  faq.question,
                  faq.question_translations,
                  currentLang
                );
                const translatedAnswer = getTranslatedText(
                  faq.answer,
                  faq.answer_translations,
                  currentLang
                );

                return (
                  <div
                    key={faq.id}
                    className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-primary-300 transition-all duration-200"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-lg font-semibold text-gray-900">{translatedQuestion}</span>
                      <svg
                        className={`w-6 h-6 text-primary-600 flex-shrink-0 transition-transform duration-200 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-5 text-gray-700 leading-relaxed animate-fade-in">
                        {translatedAnswer}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">{t('faq.still_questions')}</p>
            <Link
              to={createLink('contact')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 border-2 border-primary-200 rounded-lg font-semibold hover:border-primary-400 hover:bg-primary-50 transition-all duration-200"
            >
              {t('faq.contact_support')}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-white">
        <div className="container-marketing max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
                {t('resources.title')}
              </span>
            </h2>
            <p className="text-xl text-gray-600">
              {t('resources.subtitle')}
            </p>
          </div>

          {featuredPosts.length > 0 ? (
            <div className={`grid gap-8 ${featuredPosts.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' : featuredPosts.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : 'md:grid-cols-3'}`}>
              {featuredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={createLink(`blog/${post.slug}`)}
                  className="group bg-gradient-to-br from-primary-50 to-purple-50 rounded-2xl p-8 border-2 border-primary-200 hover:border-primary-400 transition-all duration-200 hover:shadow-xl"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
                      📚 {post.category}
                    </div>
                    <div className="text-sm text-gray-600">{post.read_time} min read</div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-700 mb-4 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-primary-600 font-semibold">
                    {t('resources.blog_post.cta')}
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {/* Fallback: hardcoded cards shown until posts are featured via admin */}
              <Link
                to={createLink('blog/how-to-prevent-facebook-ad-bans')}
                className="group bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200 hover:border-red-400 transition-all duration-200 hover:shadow-xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    📚 {t('resources.blog_post.badge')}
                  </div>
                  <div className="text-sm text-gray-600">{t('resources.blog_post.read_time')}</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {t('resources.blog_post.title')}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{t('resources.blog_post.description')}</p>
                <div className="flex items-center gap-2 text-primary-600 font-semibold">
                  {t('resources.blog_post.cta')}
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </Link>

              <Link
                to={createLink('vs/adspect')}
                className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200 hover:border-blue-400 transition-all duration-200 hover:shadow-xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                    ⚖️ {t('resources.comparison.badge')}
                  </div>
                  <div className="text-sm text-gray-600">{t('resources.comparison.updated')}</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {t('resources.comparison.title')}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{t('resources.comparison.description')}</p>
                <div className="flex items-center gap-2 text-primary-600 font-semibold">
                  {t('resources.comparison.cta')}
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </Link>

              <Link
                to={createLink('click-fraud-protection')}
                className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200 hover:border-green-400 transition-all duration-200 hover:shadow-xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    🛡️ {t('resources.click_fraud.badge')}
                  </div>
                  <div className="text-sm text-gray-600">{t('resources.click_fraud.read_time')}</div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {t('resources.click_fraud.title')}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{t('resources.click_fraud.description')}</p>
                <div className="flex items-center gap-2 text-primary-600 font-semibold">
                  {t('resources.click_fraud.cta')}
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-primary-700 to-primary-900 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
        </div>

        <div className="container-marketing text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>
          <Link
            to={createLink('register')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-200"
          >
            {t('cta.button_trial')}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <p className="mt-4 text-blue-200 text-sm">
            {t('cta.no_credit_card')}
          </p>
        </div>
      </section>
    </div>
  );
}
